<template>
    <div class="nc-history-panel">
        <div class="nc-history-panel__header">
            <span class="nc-history-panel__title">Предыдущие сообщения</span>
            <button class="nc-history-panel__close" type="button" @click="$emit('close')">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                </svg>
            </button>
        </div>

        <div ref="list" class="nc-history-panel__list">
            <div v-if="isLoading" class="nc-history-panel__loader">
                <v-preloader class="preloader_type_button" size="small" />
            </div>

            <template v-else>
                <div v-if="!messages.length" class="nc-history-panel__empty">
                    Нет сообщений
                </div>
                <template v-else>
                    <div v-if="isLoadingMore" class="nc-history-panel__loader">
                        <v-preloader class="preloader_type_button" size="small" />
                    </div>
                    <div v-else-if="!hasMore" class="nc-history-panel__end">
                        Начало истории
                    </div>

                    <div v-for="msg in messages" :key="msg.id" class="nc-history-panel__row">
                        <new-chat-message-event v-if="msg.type === 'event'" :message="msg" />
                        <new-chat-message-media
                            v-else-if="msg.type === 'media'"
                            :message="msg"
                            @open="viewerAttachment = $event"
                            @file-downloaded="_onFileDownloaded"
                        />
                        <new-chat-message-text v-else-if="msg.type === 'text'" :message="msg" />
                    </div>
                </template>
            </template>
        </div>

        <new-chat-attachment-viewer
            :attachment="viewerAttachment"
            @close="viewerAttachment = null"
        />
    </div>
</template>

<script>
import api from '@api'
import vPreloader from '../../global/Preloader.vue'
import NewChatMessageEvent from './messages/NewChatMessageEvent.vue'
import NewChatMessageText from './messages/NewChatMessageText.vue'
import NewChatMessageMedia from './messages/NewChatMessageMedia.vue'
import NewChatAttachmentViewer from './NewChatAttachmentViewer.vue'

export default {
    name: 'NewChatHistoryPanel',
    components: { vPreloader, NewChatMessageEvent, NewChatMessageText, NewChatMessageMedia, NewChatAttachmentViewer },
    props: {
        customerId: { type: [Number, String], required: true },
        chatId:     { type: [Number, String], default: null },
    },
    emits: ['close'],
    data() {
        return {
            messages:         [],
            currentPage:      1,
            hasMore:          true,
            isLoading:        true,
            isLoadingMore:    false,
            viewerAttachment: null,
        }
    },
    created() {
        this._ignoringScroll = false
        this._prepending     = false
        this._loadingMore    = false
        this._scrollHandler  = () => this._onScroll()
    },
    mounted() {
        this._loadPage(1)
    },
    beforeDestroy() {
        const el = this.$refs.list
        if (el) el.removeEventListener('scroll', this._scrollHandler)
    },
    methods: {
        _loadPage(page) {
            if (page === 1) {
                this.isLoading = true
            } else {
                this._loadingMore  = true
                this.isLoadingMore = true
            }

            api.newChat.getHistoryMessages(this.customerId, page)
                .then(response => {
                    const msgs = Array.isArray(response) ? response : (response.messages || [])

                    if (page === 1) {
                        this.messages    = msgs
                        this.currentPage = 1
                        this.hasMore     = msgs.length > 0
                        this.isLoading   = false
                        this.$nextTick(() => {
                            this._attachScrollListener()
                            this._scrollToBottom()
                        })
                    } else {
                        const existingIds = new Set(this.messages.map(m => m.id))
                        const fresh = msgs.filter(m => !existingIds.has(m.id))

                        if (fresh.length === 0) {
                            this.hasMore = false
                        } else {
                            const el              = this.$refs.list
                            const prevScrollHeight = el ? el.scrollHeight : 0

                            this._prepending   = true
                            this._loadingMore  = false
                            this.isLoadingMore = false
                            this.messages      = [...fresh, ...this.messages]
                            this.currentPage   = page

                            this.$nextTick(() => {
                                if (!el || !el.isConnected) return
                                this._ignoringScroll = true
                                el.scrollTop = el.scrollHeight - prevScrollHeight
                                this._prepending = false
                                this.$nextTick(() => { this._ignoringScroll = false })
                            })
                            return
                        }
                    }
                })
                .catch(err => console.error('[NewChat] historyMessages error:', err))
                .finally(() => {
                    this.isLoading     = false
                    this._loadingMore  = false
                    this.isLoadingMore = false
                })
        },

        _attachScrollListener() {
            const el = this.$refs.list
            if (!el || el._hasScrollListener) return
            el.addEventListener('scroll', this._scrollHandler)
            el._hasScrollListener = true
        },

        _scrollToBottom() {
            const el = this.$refs.list
            if (!el) return
            this._ignoringScroll = true
            el.scrollTop = el.scrollHeight
            this.$nextTick(() => { this._ignoringScroll = false })
        },

        _onScroll() {
            if (this._ignoringScroll || this._loadingMore || !this.hasMore || this.isLoading) return
            const el = this.$refs.list
            if (!el || el.scrollTop >= 80) return
            this._loadPage(this.currentPage + 1)
        },

        _onFileDownloaded({ id, message }) {
            const idx = this.messages.findIndex(m => m.id === id)
            if (idx !== -1) {
                this.$set(this.messages, idx, { ...this.messages[idx], message })
            }
        },
    },
}
</script>

<style lang="scss">
@import '../../../../../gulp-src/app/scss/_variables';

.nc-history-panel {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    max-height: 280px;
    background: $global-white-f6;
    border-bottom: 1px solid $global-light-gray;

    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 6px 12px;
        border-bottom: 1px solid $global-light-gray;
        flex: 0 0 auto;
    }

    &__title {
        font-size: 12px;
        font-weight: 600;
        color: $global-gray;
        text-transform: uppercase;
        letter-spacing: 0.4px;
    }

    &__close {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        border: none;
        background: none;
        cursor: pointer;
        color: $global-gray;
        border-radius: 4px;
        transition: background 0.15s, color 0.15s;
        padding: 0;

        &:hover {
            background: $global-light-gray;
            color: $global-black;
        }
    }

    &__list {
        flex: 1 1 auto;
        overflow-y: auto;
        padding: 8px 0;
    }

    &__row {
        display: contents;
    }

    &__loader {
        display: flex;
        justify-content: center;
        padding: 10px 0;
    }

    &__empty,
    &__end {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 10px;
        font-size: 12px;
        color: $global-gray;
        opacity: 0.7;
    }
}
</style>
